import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { visibleFor } from "./NavShell.js";
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
function DrawerLink({ item, activeKey, onNavigate, onNavigateAndClose, }) {
    return (_jsxs("a", { href: item.href, className: ["sl-mobile-drawer-nav__link", item.key === activeKey ? "sl-mobile-drawer-nav__link--active" : null]
            .filter(Boolean)
            .join(" "), "aria-current": item.key === activeKey ? "page" : undefined, onClick: (event) => {
            if (onNavigate) {
                event.preventDefault();
                onNavigate(item);
            }
            // Close after `onNavigate` fires (spec: "Close on: ... after onNavigate fires"),
            // regardless of whether the caller intercepted the click or let the anchor's
            // default navigation proceed.
            onNavigateAndClose();
        }, children: [item.icon, _jsx("span", { children: item.label })] }));
}
/**
 * Hamburger trigger + slide-in overlay drawer, consuming the exact same `NavItem`/
 * `NavSection` data shape `NavShell` takes (reusing its `visibleFor` role filter rather
 * than redefining it) — meant to be dropped in alongside an existing `NavShell` layout
 * below a mobile breakpoint, not a replacement for `NavShell` itself.
 *
 * Focus handling is intentionally lightweight rather than a full focus-trap library:
 * opening moves focus into the panel, closing restores it to the trigger button, and a
 * `Tab`/`Shift+Tab` keydown handler on the panel cycles focus among the panel's own
 * focusable elements so keyboard focus never visibly lands on whatever page content sits
 * behind the (visually full-screen, high-z-index) backdrop.
 */
export function MobileDrawerNav({ role, items, sections, activeKey, onNavigate, header, footer }) {
    const [open, setOpen] = useState(false);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);
    const visibleItems = visibleFor(role, items);
    const visibleSections = (sections ?? [])
        .map((section) => ({ ...section, items: visibleFor(role, section.items) }))
        .filter((section) => section.items.length > 0);
    function close() {
        setOpen(false);
    }
    function handleNavigateAndClose() {
        close();
    }
    // Open: move focus into the panel. Close: restore focus to the trigger, so a keyboard
    // or screen-reader user returns to exactly where they were, not to the top of the page.
    useEffect(() => {
        if (open) {
            panelRef.current?.focus();
            // Prevent the page behind the overlay from scrolling while the drawer is open.
            const previousOverflow = document.body.style.overflow;
            document.body.style.overflow = "hidden";
            return () => {
                document.body.style.overflow = previousOverflow;
            };
        }
        triggerRef.current?.focus();
        return undefined;
    }, [open]);
    useEffect(() => {
        if (!open)
            return undefined;
        function handleKeyDown(event) {
            if (event.key === "Escape") {
                close();
            }
        }
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [open]);
    function handlePanelKeyDown(event) {
        if (event.key !== "Tab" || !panelRef.current)
            return;
        const focusable = Array.from(panelRef.current.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => el.offsetParent !== null || el === document.activeElement);
        if (focusable.length === 0)
            return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        }
        else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }
    return (_jsxs("div", { className: "sl-mobile-drawer-nav", children: [_jsx("button", { type: "button", ref: triggerRef, className: "sl-mobile-drawer-nav__trigger", "aria-label": "Open menu", "aria-expanded": open, onClick: () => setOpen(true), children: _jsxs("svg", { viewBox: "0 0 24 24", width: "24", height: "24", fill: "none", stroke: "currentColor", strokeWidth: "2", "aria-hidden": "true", children: [_jsx("line", { x1: "3", y1: "6", x2: "21", y2: "6", strokeLinecap: "round" }), _jsx("line", { x1: "3", y1: "12", x2: "21", y2: "12", strokeLinecap: "round" }), _jsx("line", { x1: "3", y1: "18", x2: "21", y2: "18", strokeLinecap: "round" })] }) }), open ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "sl-mobile-drawer-nav__backdrop", onClick: close, "aria-hidden": "true" }), _jsxs("div", { className: "sl-mobile-drawer-nav__panel", role: "dialog", "aria-modal": "true", "aria-label": "Menu", ref: panelRef, tabIndex: -1, onKeyDown: handlePanelKeyDown, children: [_jsxs("div", { className: "sl-mobile-drawer-nav__panel-header", children: [header ? _jsx("div", { className: "sl-mobile-drawer-nav__header", children: header }) : null, _jsx("button", { type: "button", className: "sl-mobile-drawer-nav__close", "aria-label": "Close menu", onClick: close, children: _jsxs("svg", { viewBox: "0 0 24 24", width: "22", height: "22", fill: "none", stroke: "currentColor", strokeWidth: "2", "aria-hidden": "true", children: [_jsx("line", { x1: "5", y1: "5", x2: "19", y2: "19", strokeLinecap: "round" }), _jsx("line", { x1: "19", y1: "5", x2: "5", y2: "19", strokeLinecap: "round" })] }) })] }), _jsxs("ul", { className: "sl-mobile-drawer-nav__list", children: [visibleItems.map((item) => (_jsx("li", { children: _jsx(DrawerLink, { item: item, activeKey: activeKey, onNavigate: onNavigate, onNavigateAndClose: handleNavigateAndClose }) }, item.key))), visibleSections.map((section) => (_jsxs("li", { className: "sl-mobile-drawer-nav__section", children: [_jsx("p", { className: "sl-mobile-drawer-nav__section-label", children: section.label }), _jsx("ul", { className: "sl-mobile-drawer-nav__section-list", children: section.items.map((item) => (_jsx("li", { children: _jsx(DrawerLink, { item: item, activeKey: activeKey, onNavigate: onNavigate, onNavigateAndClose: handleNavigateAndClose }) }, item.key))) })] }, section.key)))] }), footer ? _jsx("div", { className: "sl-mobile-drawer-nav__footer", children: footer }) : null] })] })) : null] }));
}
//# sourceMappingURL=MobileDrawerNav.js.map